// =========================================================================
// The custody screens' decisions, as plain functions with tests — what a
// card may offer, which group an address book entry sits in, what a pool
// total says, what the Flow's Pay step says, and the gate that keeps a
// signing link's answer from landing in a sheet opened for another card
// (Screens' Astra review of 22 Oct 2026).
// =========================================================================
import type { ApprovalRow, WhitelistEntry, WalletBalance, MoveLimits, PayoutSource } from './api';
import { CLASS_WORD, amountProblem } from './custody-format';
import { ApiError } from './errors';
import { fmtMoney } from './format';
import { CARD_APPROVAL_WINDOW_MS } from '../../../signer/src/card-approval.mjs';

/**
 * A gate for minted links: only the answer to the LATEST opening of a sheet
 * is shown. Open card A, close, open card B — A's link may resolve last, and
 * without this it would show as B's. A close invalidates everything in
 * flight; a failure in flight for a superseded opening is dropped, not shown.
 */
export function makeMintGate() {
  let generation = 0;
  return {
    /** A claim on the current opening: the function it returns says whether that opening is still the latest. A long operation (a signature on the device) checks it before showing its result. */
    claim(): () => boolean {
      const mine = generation;
      return () => mine === generation;
    },
    async open<T>(mint: () => Promise<T>): Promise<{ stale: true } | { stale: false; value: T }> {
      const mine = ++generation;
      try {
        const value = await mint();
        return mine === generation ? { stale: false, value } : { stale: true };
      } catch (err) {
        if (mine === generation) throw err;
        return { stale: true };
      }
    },
    close() { generation++; },
  };
}

/**
 * What a card offers: a signature by cable, a review on a phone, the second
 * device's approval — or nothing, and why. ONE decision for every entry
 * point: a card refused here offers no button at all, the approval included
 * (Screens' round 2, 23 Oct 2026: the approve button stood beside "do not sign").
 */
/**
 * Payouts by cable — the operator, 29 Oct 2026 (N-089): "Yes, cable until the Stax". His Gen 5 cannot pair with TronLink
 * over Bluetooth, so a payout is signed by cable at the laptop, like the desk's own moves, until a Stax arrives; then this
 * goes back to false (payouts by phone only — his ruling of 22 Oct 2026, Q1). The server takes either.
 */
export const PAYOUTS_BY_CABLE = true;
/** Whether a card of this kind may be signed by cable on this dashboard. */
export const cableSigns = (kind: string): boolean => kind !== 'payout' || PAYOUTS_BY_CABLE;

export type CardActions = { sign: boolean; review: boolean; approve: boolean; reason: 'incomplete' | 'expired' | 'unknown_payee' | null };

export function cardActions(a: Pick<ApprovalRow, 'kind' | 'partyName' | 'toAddress' | 'amount' | 'approval'>, expired: boolean): CardActions {
  const none = { sign: false, review: false, approve: false } as const;
  if (a.toAddress === null || a.amount === null) return { ...none, reason: 'incomplete' };
  if (expired) return { ...none, reason: 'expired' };
  // A payout to an address the desk cannot name is refused on the card itself:
  // no entry point is offered, so "do not sign" has no button beside it.
  if (a.kind === 'payout' && !a.partyName) return { ...none, reason: 'unknown_payee' };
  // The second device's word (N-068): asked for while required and not yet given.
  const approve = a.approval?.required === true && !a.approval.by;
  return { sign: true, review: true, approve, reason: null };
}

/**
 * How a card's latest second-device approval ended:
 *   done        the server took it;
 *   failed      a verified failure — the server's decision (a reason), or a
 *               refusal before the device signed — nothing was recorded;
 *   unknown     the device signed and the submission began, then the answer
 *               was lost (a dead connection, an unreadable answer, a gateway
 *               error): the server MAY have recorded it, and the attempt may
 *               still be running. The list says: approved means recorded. A
 *               read that shows the card still open proves only the order of
 *               answers, not that the attempt is over (Screens' round 5) —
 *               until the attempt CANNOT record any more: the server refuses
 *               an approval message older than CARD_APPROVAL_WINDOW_MS, so
 *               past `since` + that window a read still showing the card open
 *               is authoritative;
 *   unrecorded  that: the window passed, the list still shows the card open —
 *               the approval was not recorded — approve again.
 */
export type ApprovalEnd =
  | { at: 'done' }
  | { at: 'failed'; error: unknown }
  | { at: 'unknown'; error: unknown; since: number }
  | { at: 'unrecorded' };

/** How far an attempt got when it failed: before the device signed, signed but not yet submitted, or submitted. */
export type ApprovalStage = 'before' | 'signed' | 'submitted';

/** What a failure at a stage means: the server's reasoned answer is a decision at any stage; otherwise only a failure before submission is verified. */
export function approvalEndOf(error: unknown, stage: ApprovalStage, at: number): ApprovalEnd {
  if (error instanceof ApiError && typeof error.body.reason === 'string' && error.body.reason) return { at: 'failed', error };
  if (stage === 'submitted') return { at: 'unknown', error, since: at };
  return { at: 'failed', error };
}

/** How long a lost attempt may still record: the server's own window on the approval message (signer/src/card-approval.mjs). */
export const APPROVAL_SETTLE_MS = CARD_APPROVAL_WINDOW_MS;
/** A read whose request time is not known (a poll): it prunes, and settles no unknown. */
export const READ_UNTIMED = Number.NEGATIVE_INFINITY;

/**
 * The outcomes still worth showing after an AUTHORITATIVE read of the list:
 * an outcome stays with its card — a refusal of card A is not forgotten
 * because card B was started, nor a success because a refresh failed — and
 * goes only when the list no longer has the card, or shows it approved. An
 * unknown outcome is settled by the list: the card approved means it was
 * recorded (and the card says by whom); the card still open settles nothing
 * while the attempt may still record, and means "not recorded" only from a
 * read whose REQUEST STARTED after the server's window on that attempt had
 * passed — `startedAt` is when the request was sent, never when its answer
 * arrived: a GET that snapshotted the card open before the deadline can be
 * delivered after it, after the attempt committed (Screens' rounds 3–6,
 * 23 Oct 2026). A poll's start is not known: READ_UNTIMED.
 */
export function endedStillShown(ended: Readonly<Record<string, ApprovalEnd>>, rows: ReadonlyArray<Pick<ApprovalRow, 'txid' | 'approval'>>, startedAt: number = READ_UNTIMED): Record<string, ApprovalEnd> {
  const open = new Set(rows.filter((r) => !r.approval?.by).map((r) => r.txid));
  const kept: Record<string, ApprovalEnd> = {};
  for (const [txid, end] of Object.entries(ended)) {
    if (!open.has(txid)) continue;
    kept[txid] = end.at === 'unknown' && startedAt > end.since + APPROVAL_SETTLE_MS ? { at: 'unrecorded' } : end;
  }
  return kept;
}

/** The screen's approval state: one device operation at a time, and how each card's latest attempt ended. */
export type ApprovalState = { busy: string | null; step: string | null; ended: Readonly<Record<string, ApprovalEnd>> };
export const APPROVAL_IDLE: ApprovalState = { busy: null, step: null, ended: {} };
export type ApprovalAction =
  | { type: 'start'; txid: string }
  | { type: 'step'; text: string }
  | { type: 'done'; txid: string }
  | { type: 'failed'; txid: string; error: unknown; stage: ApprovalStage; at: number }
  /** `at` is when the read's REQUEST STARTED (READ_UNTIMED for a poll). */
  | { type: 'read'; rows: ReadonlyArray<Pick<ApprovalRow, 'txid' | 'approval'>>; at: number };

/**
 * The approval's lifecycle as one pure step, so the sequence Screens named
 * can be run as a test: refuse A, start and finish B, a failed refresh (no
 * read), an authoritative read. A new attempt on a card replaces how its
 * last one ended; only a read supersedes an outcome; the lock is released
 * whichever way an attempt ends.
 */
/** The earliest moment a read can settle an unknown outcome — the attempt's time plus the server's window — or null with none unknown. */
/**
 * The list's own refetch, as the hook calls it. React Query RESOLVES a failed
 * refetch with an error result that still carries the cached rows (Screens'
 * round 7, 23 Oct 2026): read as a fresh post-deadline look at the open card,
 * that cache turned an unknown outcome into "approve again". So the hook asks
 * it to throw, and dispatches nothing on an error result either way.
 */
export type ListRefetch = (o?: { throwOnError?: boolean }) => Promise<{ data?: { approvals: ApprovalRow[] } | undefined; isError?: boolean }>;

/** A read the hook starts: stamped with its REQUEST's start, or null when nothing was read — the outcome stays as it is. */
export async function readStartedAt(refetch: ListRefetch, startedAt: number): Promise<ApprovalAction | null> {
  try {
    const result = await refetch({ throwOnError: true });
    if (result.isError || !result.data) return null;
    return { type: 'read', rows: result.data.approvals, at: startedAt };
  } catch {
    return null;
  }
}

export function approvalDeadline(ended: Readonly<Record<string, ApprovalEnd>>): number | null {
  let earliest: number | null = null;
  for (const end of Object.values(ended)) {
    if (end.at !== 'unknown') continue;
    const at = end.since + APPROVAL_SETTLE_MS;
    if (earliest === null || at < earliest) earliest = at;
  }
  return earliest;
}

export function approvalReducer(s: ApprovalState, a: ApprovalAction): ApprovalState {
  switch (a.type) {
    case 'start': {
      const { [a.txid]: _gone, ...rest } = s.ended;
      return { busy: a.txid, step: null, ended: rest };
    }
    case 'step': return { ...s, step: a.text };
    case 'done': return { busy: null, step: null, ended: { ...s.ended, [a.txid]: { at: 'done' } } };
    case 'failed': return { busy: null, step: null, ended: { ...s.ended, [a.txid]: approvalEndOf(a.error, a.stage, a.at) } };
    case 'read': return { ...s, ended: endedStillShown(s.ended, a.rows, a.at) };
    default: return s;
  }
}

/**
 * The typed confirmation of a destination holds for the card and the address
 * it was typed for, and for nothing else: the sheet re-reads its card at the
 * tap, and an ending typed for card A must not unlock card B (Screens' round 2).
 */
export function confirmationHolds(typed: { txid: string; address: string } | null, card: { txid: string; to: string | null }): boolean {
  return typed !== null && card.to !== null && typed.txid === card.txid && typed.address === card.to;
}

/**
 * What a list shows from its query: a read that failed AFTER one succeeded
 * keeps the list on screen — stale, with the failure said above it — rather
 * than replacing it. Replacing it unmounted a card whose device operation
 * was in flight, and a sheet whose request id a retry would then renew
 * (Screens' round 2). Only a read that never succeeded shows the failure alone.
 */
export function listState<T>(q: { isError: boolean; data: T | undefined }): { show: 'failed' | 'pending' | 'ready'; stale: boolean } {
  if (q.data !== undefined) return { show: 'ready', stale: q.isError };
  return { show: q.isError ? 'failed' : 'pending', stale: false };
}

/**
 * Where a payout to this entry may leave from (contracts/wallet.mjs
 * sourceMayPay; the operator, 22 Oct 2026): Hot pays any approved address,
 * customer or merchant (his word, 23 Oct 2026: "yes"); Merchant Hot and
 * Flagged pay only a party the Books mark MERCHANT. An entry whose standing
 * is not known (an older Worker) is paid from Hot only.
 *
 * The FIRST entry is the sheet's default. For a merchant that is Merchant Hot
 * (the operator, 23 Oct 2026, after a merchant payout left from Hot because
 * Hot was preselected): the money a merchant is paid with is the merchant
 * float's, Hot is the choice, not the default.
 */
export type PayoutSourceClass = 'running_hot' | 'dirty_hot' | 'frozen';
export function paySources(e: { partyRole?: 'customer' | 'merchant' | null }): PayoutSourceClass[] {
  return e.partyRole === 'merchant' ? ['dirty_hot', 'running_hot', 'frozen'] : ['running_hot'];
}

/**
 * The lists a deposit's rating names, as information lines (the operator,
 * 22 Oct 2026: "anything on a UAE or UN list … I haven't asked you to do
 * this" — a list hit is shown, never refused): the sender on a list,
 * blacklisted by a venue, money through listed parties. Nothing when the
 * rating names none, or an older Worker sent none.
 */
export function listLines(d: { kytLists?: { senderListed: { listing: string; label: string } | null; blacklisted: Array<{ by: string; address: string | null }>; sanctions: Array<{ listing: string; label: string; usd: number | null }> } | null }): string[] {
  const l = d.kytLists;
  if (!l) return [];
  const lines: string[] = [];
  if (l.senderListed) lines.push(`Sender on a list · ${listed(l.senderListed)}`);
  for (const b of l.blacklisted) lines.push(`Blacklisted by ${b.by}`);
  // The figure by HOUSE-STYLE rule 10 (N-084, "screens too"): "1,050 USD", unit after, no "$".
  for (const s of l.sanctions) lines.push(`Listed money through · ${listed(s)}${s.usd !== null ? ` · ${fmtMoney(s.usd, 'USD')}` : ''}`);
  return lines;
}

/** The list's own name stays beside the label (Screens' sources review, 23 Oct 2026: "UAE" was dropped) — once, when the label does not carry it already. */
function listed(x: { listing: string; label: string }): string {
  const listing = x.listing.trim();
  const label = x.label.trim();
  if (!listing) return label;
  if (!label) return listing;
  // The list's name as a word of its own in the label (UN in "UN list", not in "United"): delimited by space, punctuation or an edge.
  const token = new RegExp(`(^|[\\s·:;,()\\-—–/])${listing.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=$|[\\s·:;,()\\-—–/])`, 'i');
  return token.test(label) ? label : `${listing} · ${label}`;
}

/** The deposits that carry a list hit, oldest first — for the screen's top line, so one behind many clean rows is still seen. */
export function listedDeposits<T extends { id: number; detectedAt: string; kytLists?: Parameters<typeof listLines>[0]['kytLists'] }>(rows: readonly T[]): T[] {
  return rows.filter((d) => listLines(d).length > 0).sort((a, b) => a.detectedAt.localeCompare(b.detectedAt));
}

// ---- the move sheet (Screens' sources reviews, 23 Oct 2026) -----------------
//
// A payment is an ATTEMPT: an immutable snapshot — destination, amount, source
// — under ONE key (db/0085 idempotency_key), with a generation number per
// transmission. Its state:
//   pending    sent, no answer yet — the fields are locked;
//   uncertain  no reasoned answer (nothing, or an answer with no reason): it
//              MAY be recorded — the fields stay locked, the next press sends
//              the same snapshot under the same key, and a changed payment
//              goes only after the earlier press is reconciled in Approvals;
//   refused    the server's reasoned no: nothing recorded — the fields are
//              free, an edit gets a new key, the refusal keeps its own source;
//   queued     recorded.
// A result names the transmission it answers (seq): an older one is ignored.
// A refusal of a RECOVERING transmission (a same-key retry) says nothing about
// the original — the door checks the window and the destination before the
// reservation — so the attempt stays uncertain until the desk says it is
// recorded, or the key is VOIDED on the desk (db/0093: under the key's own
// lock, so a request still travelling is refused after it) and a changed
// payment goes under a new key. An unresolved attempt survives the sheet
// closing and a route change (the store below) and is restored on the next
// opening.

export type MoveAttempt = { seq: number; requestId: string; toWalletId: number; amount: string; from: PayoutSource;
  /** Out of Flagged: the depositor whose held money it is (db/0103 held_for); part of the payment, replayed with it. */
  heldFor?: string | null };
export type AttemptState = 'pending' | 'uncertain' | 'refused' | 'queued';
/**
 * A recorded press as the desk holds it: queued — still to sign, or being
 * built (an expired card is REBUILT by the desk, signed or not: the same
 * payment, a new card); sending — broadcast, which the desk commits before
 * the chain answers and takes back to queued if the chain refuses; sent —
 * swept, the one completion. Failed is the one terminal "not paid".
 */
export type RecordedOutcome = 'queued' | 'sending' | 'sent';
export type SheetAttempt = MoveAttempt & { state: AttemptState; recovering: boolean; error: unknown; outcome?: RecordedOutcome };
/** What a row's status means for the press (db/0076 statuses; orchestrator/src/pipeline/sweep.mjs for what each one becomes). */
export function recordedOutcome(status: string | null | undefined): RecordedOutcome | 'not_paid' {
  if (status === 'swept') return 'sent';
  if (status === 'broadcast') return 'sending';
  if (status === 'failed') return 'not_paid';
  return 'queued';
}
export type MoveSheetState = {
  destinationId: number | null; requestId: string; seq: number; attempt: SheetAttempt | null;
  /**
   * What the store still has to forget, by key: added by a settling
   * transition only — the desk's word, a void, a refusal — never by closing
   * or moving to another payee; dropped only when the persistence effect
   * says it applied them (`acked`). Durable across a batch of actions
   * (Screens' round 7, 23 Oct 2026: a flag reset by every action was erased
   * when two actions landed in one render, and the settled press came back).
   */
  forgets: Forget[];
};
export type Forget = { destinationId: number; requestId: string };
export type MoveSheetAction =
  | { type: 'submit'; attempt: MoveAttempt }
  /** The door answered this transmission, with the row's status (same_press included): settled by it, through the same words as the desk's. */
  | { type: 'done'; seq: number; status: string | null; newId: string }
  /** `newId` is taken only when the failure is terminal for the key (request_voided: the key was closed elsewhere). */
  | { type: 'failed'; seq: number; error: unknown; newId: string }
  /** The desk says the press under this key is recorded (GET moves/by-request, the void door), with the row's status: settled by it, whatever transmission is in flight. */
  | { type: 'recorded'; requestId: string; status: string | null; newId: string }
  /** The desk closed the key (POST moves/by-request/:id/void, db/0093): nothing under it can be recorded any more — a changed payment may go, under a new key. */
  | { type: 'voided'; requestId: string; newId: string }
  /** The operator edited the amount or the source: admitted with no attempt or a refused one — the payment differs, so a new key. */
  | { type: 'edit'; newId: string }
  /** The sheet closed: a tracked attempt is kept; anything else is forgotten by the sheet (the store keeps nothing of it). */
  | { type: 'close'; newId: string }
  /** The persistence effect applied these deletions: they leave the list. */
  | { type: 'acked'; requestIds: string[] }
  /**
   * The sheet opened for a payee: its own tracked attempt (this sheet's, or
   * the store's) comes back locked; another payee's attempt stays in the store
   * under its own destination and this sheet starts clean for this one.
   */
  | { type: 'open'; destinationId: number; kept: MoveAttempt | null; newId: string };

export type AttemptEnd = 'refused' | 'uncertain';
const reasonOf = (error: unknown): string | null => (error instanceof ApiError && typeof (error.body as { reason?: unknown }).reason === 'string' ? (error.body as { reason: string }).reason : null);
export function attemptEnd(error: unknown): AttemptEnd {
  return reasonOf(error) !== null ? 'refused' : 'uncertain';
}
export const moveSheetIdle = (requestId: string): MoveSheetState => ({ destinationId: null, requestId, seq: 0, attempt: null, forgets: [] });
/** One deletion per key: a second settlement of the same press adds nothing. */
const forgetting = (list: Forget[], a: { toWalletId: number; requestId: string }): Forget[] =>
  list.some((f) => f.requestId === a.requestId) ? list : [...list, { destinationId: a.toWalletId, requestId: a.requestId }];
const snapshot = (a: MoveAttempt): MoveAttempt => ({ seq: a.seq, requestId: a.requestId, toWalletId: a.toWalletId, amount: a.amount, from: a.from, ...(a.heldFor ? { heldFor: a.heldFor } : {}) });
const isUnresolved = (a: SheetAttempt | null): a is SheetAttempt => a !== null && (a.state === 'pending' || a.state === 'uncertain');
/** Tracked with the desk by its key: unresolved, or sent and not yet confirmed by the chain. */
const isTracked = (a: SheetAttempt | null): a is SheetAttempt => a !== null && (a.state === 'pending' || a.state === 'uncertain' || (a.state === 'queued' && a.outcome === 'sending'));

/**
 * The desk's word on a recorded press settles it. The transmission counter
 * moves past every answer in flight, so a late POST answer for the same
 * transmission changes nothing afterwards. A terminal failure takes a fresh
 * key: the next press, unchanged, is a new payment, never the same_press of
 * a failed row. What the store forgets is said explicitly (forget).
 */
function settle(s: MoveSheetState, status: string | null, newId: string): MoveSheetState {
  const a = s.attempt!;
  const seq = s.seq + 1;
  const outcome = recordedOutcome(status);
  if (outcome === 'not_paid') {
    return { ...s, seq, requestId: newId, forgets: forgetting(s.forgets, a), attempt: { ...a, seq, state: 'refused', error: new ApiError(409, { error: 'conflict', reason: `move_${status}` }) } };
  }
  // Sending: still tracked (broadcast goes back to queued if the chain refuses), so the store keeps it.
  return { ...s, seq, forgets: outcome === 'sending' ? s.forgets : forgetting(s.forgets, a), attempt: { ...a, seq, state: 'queued', outcome, error: null } };
}

export function moveSheetReducer(s: MoveSheetState, a: MoveSheetAction): MoveSheetState {
  switch (a.type) {
    case 'submit': {
      const recovering = s.attempt !== null && s.attempt.state === 'uncertain' && s.attempt.requestId === a.attempt.requestId;
      return { ...s, requestId: a.attempt.requestId, seq: a.attempt.seq, attempt: { ...snapshot(a.attempt), state: 'pending', recovering, error: null } };
    }
    case 'done':
      if (!s.attempt || a.seq !== s.attempt.seq) return s;
      return settle(s, a.status, a.newId);
    case 'failed': {
      if (!s.attempt || a.seq !== s.attempt.seq) return s;
      // The key closed elsewhere (another tab, another device): terminal for this key — free, under a new one.
      if (reasonOf(a.error) === 'request_voided') {
        return { ...s, seq: s.seq + 1, requestId: a.newId, forgets: forgetting(s.forgets, s.attempt), attempt: { ...s.attempt, state: 'refused', error: a.error } };
      }
      const end = s.attempt.recovering ? 'uncertain' : attemptEnd(a.error);
      // A reasoned refusal of a first transmission: nothing is recorded under the key, so the pending snapshot the
      // store was given leaves it too (Screens' round 7: a refused payment came back on reopening as uncertain, locked).
      // A recovering transmission's refusal says nothing about the original: its entry stays.
      return { ...s, forgets: end === 'refused' ? forgetting(s.forgets, s.attempt) : s.forgets, attempt: { ...s.attempt, state: end, error: a.error } };
    }
    case 'recorded':
      if (!isTracked(s.attempt) || s.attempt.requestId !== a.requestId) return s;
      return settle(s, a.status, a.newId);
    case 'voided':
      if (s.attempt?.state !== 'uncertain' || s.attempt.requestId !== a.requestId) return s;
      return { ...s, requestId: a.newId, attempt: null, forgets: forgetting(s.forgets, s.attempt) };
    // The generation counter never goes back within the sheet's life: a late answer for an earlier transmission can match nothing new.
    case 'edit':
      return !s.attempt || s.attempt.state === 'refused' ? { ...s, requestId: a.newId } : s;
    case 'close':
      return isTracked(s.attempt) ? s : { ...s, requestId: a.newId, attempt: null };
    case 'acked': {
      const left = s.forgets.filter((f) => !a.requestIds.includes(f.requestId));
      return left.length === s.forgets.length ? s : { ...s, forgets: left };
    }
    case 'open': {
      if (isTracked(s.attempt) && s.attempt.toWalletId === a.destinationId) return { ...s, destinationId: a.destinationId };
      // Deletions not yet applied survive an opening: they are the store's debt, not this payee's state.
      const clean: MoveSheetState = { destinationId: a.destinationId, requestId: a.newId, seq: s.seq, attempt: null, forgets: s.forgets };
      if (!a.kept || a.kept.toWalletId !== a.destinationId) return clean;
      // A kept attempt the sheet has already said to forget (its deletion owed, not yet applied) stays forgotten:
      // reading it back would restore a settled or refused payment as uncertain (Screens' pay sheet round 8, #2).
      if (s.forgets.some((f) => f.requestId === a.kept!.requestId)) return clean;
      // A kept attempt comes back uncertain — a pending press interrupted is unknown too — under a generation of its own here.
      const seq = Math.max(s.seq, a.kept.seq) + 1;
      return { destinationId: a.destinationId, requestId: a.kept.requestId, seq, attempt: { ...snapshot(a.kept), seq, state: 'uncertain', recovering: true, error: null }, forgets: s.forgets };
    }
  }
}
/** Through sending and recovery the amount and the source are read-only: what shows is the snapshot. */
export const fieldsLocked = (s: MoveSheetState): boolean => isUnresolved(s.attempt);
/** Still travelling, as far as this sheet knows: nothing is pressed until it answers (or the desk says it is recorded). */
export const sending = (s: MoveSheetState): boolean => s.attempt?.state === 'pending';
/** An uncertain attempt is replayed as it is — its eligibility is not a new payment's (caps, an open release). */
export const replayable = (s: MoveSheetState): boolean => s.attempt?.state === 'uncertain';
/** The attempt whose refusal is on screen, with its own source — never the current selection's. */
export const refusedAttempt = (s: MoveSheetState): SheetAttempt | null => (s.attempt?.state === 'refused' ? s.attempt : null);
/** Still tracked after a closing or a route change: what is kept for the next opening, under its own payee. */
export const unresolvedOf = (s: MoveSheetState): MoveAttempt | null => (isTracked(s.attempt) ? snapshot(s.attempt) : null);
/**
 * What the store does after a change of the attempt: a tracked one is kept
 * under ITS payee; what the reducer said to forget (a settlement, a void, a
 * terminal refusal) is forgotten by the key it had — never because the
 * sheet closed or moved to another payee (Screens' Astra review, 23 Oct
 * 2026: a switch of payee deleted an unresolved payment).
 */
export function storeStep(s: MoveSheetState): { save: MoveAttempt | null; clear: Forget[] } | null {
  const save = unresolvedOf(s);
  // Both, independently: a tracked attempt is saved under its key, and every deletion owed is applied by ITS key —
  // a clear for an older key leaves a newer attempt under the same payee alone (clearUnresolved checks the key).
  // The effect that applies the deletions dispatches `acked` with their keys; until then they stay owed.
  if (!save && s.forgets.length === 0) return null;
  return { save, clear: s.forgets };
}
/** The press being tracked with the desk by its key: pending, uncertain, or sent and not yet confirmed. */
export const trackedKey = (s: MoveSheetState): string | null => (isTracked(s.attempt) ? s.attempt.requestId : null);
/** What the next press sends: an unresolved snapshot under its key (never the form), else the form under the current key — each a new transmission. */
export function nextPayload(s: MoveSheetState, form: { toWalletId: number; amount: string; from: PayoutSource; heldFor?: string | null }): MoveAttempt {
  const seq = s.seq + 1;
  if (isUnresolved(s.attempt)) return { ...snapshot(s.attempt), seq };
  const { heldFor, ...rest } = form;
  return { seq, requestId: s.requestId, ...rest, ...(heldFor ? { heldFor } : {}) };
}
/**
 * Whether a NEW payment may be pressed — the one decision the button takes
 * (a replay of an uncertain attempt is judged separately: only not while
 * sending). Everything it is judged by must be in hand and in order.
 */
export function newPaymentBlocked(i: {
  amount: string; windowOpen: boolean; attempt: AttemptState | null; readiness: ReadState; allowance: Allowance; releaseOpen: boolean | null;
  /** Out of Flagged: whether whose held money it is, is settled (heldForPick); true for every other source. */
  heldForReady?: boolean;
}): boolean {
  if (i.attempt === 'pending' || i.attempt === 'uncertain') return true;
  if (i.amount.trim() === '' || amountProblem(i.amount) !== null) return true;
  if (!i.windowOpen) return true;
  if (i.readiness !== 'known') return true;
  if (!i.allowance.ok) return true;
  if (i.heldForReady === false) return true;
  return i.releaseOpen !== false;
}

/** One depositor whose money Flagged holds (GET /api/custody/flagged-held). */
export type FlaggedHolder = { partySlug: string; partyName: string | null; held: string };
/**
 * Whose held money a payment out of Flagged is (the Command Centre's go, 27 Oct 2026): not known while the list is
 * read; nobody's when Flagged holds no tagged depositor's money; the one depositor, pre-selected, when it holds one;
 * his choice when it holds several — nothing until he chooses. A depositor chosen (or pre-selected, which the hosts
 * keep as the choice — useHeldChoice) who is no longer held is GONE, never quietly replaced by whoever is held now
 * (Screens on cfc0d1f8, 27 Oct 2026): he chooses again. Pure.
 */
export function heldForPick(holders: ReadonlyArray<FlaggedHolder> | undefined, chosen: string | null):
  { state: 'reading' | 'none' | 'one' | 'choose' | 'chosen' | 'gone'; heldFor: string | null; ready: boolean } {
  if (holders === undefined) return { state: 'reading', heldFor: null, ready: false };
  if (chosen !== null) {
    if (!holders.some((h) => h.partySlug === chosen)) return { state: 'gone', heldFor: null, ready: false };
    return { state: holders.length === 1 ? 'one' : 'chosen', heldFor: chosen, ready: true };
  }
  if (holders.length === 0) return { state: 'none', heldFor: null, ready: false };
  if (holders.length === 1) return { state: 'one', heldFor: holders[0]!.partySlug, ready: true };
  return { state: 'choose', heldFor: null, ready: false };
}

/** A pay sheet's review, made with the depositor it named: it stands only while the payment still names that one. Pure. */
export type Reviewed = false | { heldFor: string | null };
export function reviewStands(reviewed: Reviewed, heldFor: string | null): boolean {
  return reviewed !== false && reviewed.heldFor === heldFor;
}

// The unresolved attempts, by destination — this tab's memory, and the
// session store beside it so a route change (the Approvals link) or a reload
// brings the snapshot and its key back. The store is a convenience: when it
// is not there (a private window, blocked storage) the memory still holds
// within the page; nothing here is secret (an amount, a source, a key).
const STORE_KEY = 'custody.move.unresolved';
const unresolved = new Map<number, MoveAttempt>();
let loaded = false;
const storage = (): Storage | null => { try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; } };
function loadStore(): void {
  if (loaded) return;
  loaded = true;
  try {
    const raw = storage()?.getItem(STORE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, MoveAttempt>;
    for (const [k, v] of Object.entries(parsed)) {
      if (v && typeof v.requestId === 'string' && typeof v.amount === 'string' && typeof v.from === 'string' && typeof v.toWalletId === 'number') { const { heldFor, ...rest } = v; unresolved.set(Number(k), { ...rest, seq: Number(v.seq) || 0, ...(typeof heldFor === 'string' ? { heldFor } : {}) }); }
    }
  } catch { /* an unreadable store is an empty one */ }
}
function writeStore(): void {
  try { storage()?.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(unresolved))); } catch { /* kept in memory */ }
}
export function saveUnresolved(destinationId: number, attempt: MoveAttempt): void { loadStore(); unresolved.set(destinationId, snapshot(attempt)); writeStore(); }
export function loadUnresolved(destinationId: number): MoveAttempt | null { loadStore(); return unresolved.get(destinationId) ?? null; }
export function clearUnresolved(destinationId: number, requestId?: string): void {
  loadStore();
  // With a key: only the entry under that key goes — a late word about an older press leaves a newer attempt alone.
  if (requestId !== undefined && unresolved.get(destinationId)?.requestId !== requestId) return;
  if (unresolved.delete(destinationId)) writeStore();
}
export function _resetUnresolvedForTests(): void { unresolved.clear(); loaded = false; try { storage()?.removeItem(STORE_KEY); } catch { /* none */ } }

/** What a read is, for a line: known, still waiting, or failed (the design system's three words). */
export type ReadState = 'known' | 'waiting' | 'failed';
export const readStateOf = (q: { isSuccess: boolean; isError: boolean }): ReadState => (q.isSuccess ? 'known' : q.isError ? 'failed' : 'waiting');
/**
 * A NEW payment goes only with what it is judged by in hand: the caps, and —
 * from Flagged — the cards and the register (an open release). Unread or
 * failed, the press waits and the line says which; a replay of an uncertain
 * attempt needs none of this.
 */
export function newPaymentReadiness(from: PayoutSource, reads: { limits: ReadState; approvals: ReadState; balances: ReadState }): { state: ReadState; what: string } {
  const needed: Array<[ReadState, string]> = from === 'frozen'
    ? [[reads.limits, 'the caps'], [reads.approvals, 'the open releases'], [reads.balances, 'the open releases']]
    : [[reads.limits, 'the caps']];
  const failed = needed.find(([r]) => r === 'failed');
  if (failed) return { state: 'failed', what: failed[1] };
  const waiting = needed.find(([r]) => r === 'waiting');
  if (waiting) return { state: 'waiting', what: waiting[1] };
  return { state: 'known', what: '' };
}

/** A decimal with at most six places → base units; the sheet validates the shape first (amountProblem). */
export function usdtUnits(text: string): bigint {
  const [whole, frac = ''] = text.trim().split('.');
  return BigInt((whole || '0') + (frac + '000000').slice(0, 6));
}
const grouped = (units: bigint): string => {
  const neg = units < 0n; const u = neg ? -units : units;
  const whole = (u / 1000000n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const frac = (u % 1000000n).toString().padStart(6, '0').replace(/0+$/, '');
  return `${neg ? '-' : ''}${whole}${frac ? `.${frac}` : ''}`;
};
export type Allowance = { ok: true } | { ok: false; line: string };
/**
 * What the caps allow before the press, as the door applies them
 * (bff/src/db/custody-limits.ts limitsForSource): Hot's per-move and day for
 * every source but Merchant Hot and Flagged, which have caps of their own
 * within the desk's day — Hot's day figure counts the desk; the first-payout
 * cap for a payout address no payout has reached (never for the desk's own
 * wallets: a Treasury refill has no first payout). One line, the figures in
 * it; the server's own check stays the authority. Called with the caps READ —
 * unread, the sheet waits (newPaymentReadiness) rather than guessing.
 */
export function moveAllowance(i: { amount: string; from: PayoutSource; limits: MoveLimits; stage?: WhitelistEntry['stage'] | null }): Allowance {
  const l = i.limits;
  if (i.amount.trim() === '' || amountProblem(i.amount) !== null) return { ok: true };
  const amount = usdtUnits(i.amount);
  const word = CLASS_WORD[i.from] ?? i.from;
  // The whitelist's test (db/0098): to an approved address not yet tested, only the test; while the test is out, nothing.
  // The desk's own wallets (Treasury) are not payout addresses and have no stage.
  if (i.from !== 'treasury' && i.stage === 'approved' && amount !== usdtUnits(l.testPaymentUsdt ?? '10')) {
    return { ok: false, line: `First payment here is the ${grouped(usdtUnits(l.testPaymentUsdt ?? '10'))} USDT test.` };
  }
  if (i.from !== 'treasury' && i.stage === 'test_sent') return { ok: false, line: 'Waiting for the test — press Received first.' };
  // No caps (db/0098): the caps are lifted on his word; the test above stays.
  if (l.capsOff === true) return { ok: true };
  const hotLeft = usdtUnits(l.dayCeilingUsdt) - usdtUnits(l.usedTodayUsdt);
  const own = i.from === 'dirty_hot' || i.from === 'frozen' ? l.sources?.[i.from] : null;
  const perMove = own ? usdtUnits(own.moveCeilingUsdt) : usdtUnits(l.moveCeilingUsdt);
  const ownLeft = own ? usdtUnits(own.dayCeilingUsdt) - usdtUnits(own.usedTodayUsdt) : hotLeft;
  const left = ownLeft < hotLeft ? ownLeft : hotLeft;
  if (amount > perMove) return { ok: false, line: `Over the per-move cap of ${grouped(perMove)} USDT.` };
  if (left <= 0n) return { ok: false, line: `Nothing left today from ${word}.` };
  if (amount > left) return { ok: false, line: `Only ${grouped(left)} USDT left today from ${word}.` };
  return { ok: true };
}

/**
 * Whether a release from Flagged is already waiting (db/0090: one at a time):
 * an open payout card whose source is a Flagged wallet. null = not known (a
 * list not read yet); the server refuses a second one either way.
 */
export function flaggedReleaseOpen(
  approvals: ReadonlyArray<Pick<ApprovalRow, 'kind' | 'fromAddress'>> | undefined,
  wallets: ReadonlyArray<{ class: string; address: string }> | undefined,
): boolean | null {
  if (!approvals || !wallets) return null;
  const flagged = new Set(wallets.filter((w) => w.class === 'frozen').map((w) => w.address));
  return approvals.some((a) => a.kind === 'payout' && flagged.has(a.fromAddress));
}

/**
 * Where an address book entry sits. Whitelisting has two stages (the operator, 24 Oct 2026; db/0098): the
 * owner's approval in the Books, then the 10 USDT test the customer confirms and the operator presses Received
 * for. The server says the stage (one rule, in the database); this only places it:
 *   awaiting   not approved by the owner in the Books — nothing can be paid
 *   approved   only the 10 USDT test can be paid
 *   test_sent  the test is on its way, or landed and waits for Received — nothing can be paid
 *   payable    whitelisted — normal payouts
 *   unavailable retired or archived, whatever approved it
 *   unknown    an active entry the server named no stage for — the read lacks it, so nothing is offered
 *              (Screens' round on db/0098, 26 Oct 2026: the approval fields are not a fallback; the server
 *              always reads the stage with the entry, so a missing one is a broken read, never "payable").
 */
export type WhitelistGroup = 'payable' | 'approved' | 'test_sent' | 'awaiting' | 'unavailable' | 'unknown';

export function whitelistGroup(e: Pick<WhitelistEntry, 'status'> & { stage?: WhitelistEntry['stage'] }): WhitelistGroup {
  if (e.status !== 'active') return 'unavailable';
  switch (e.stage) {
    case 'whitelisted': return 'payable';
    case 'approved': return 'approved';
    case 'test_sent': return 'test_sent';
    case 'not_approved': return 'awaiting';
    default: return 'unknown';
  }
}

/** While a test is out (Test sent), the address book is read again on this pace so a landed test shows without a reload. */
export const WHITELIST_TEST_POLL_MS = 15_000;
/** A payment recorded to an address still read as Approved: read again on this pace until its stage moves, for at most this long. */
export const WHITELIST_EXPECT_POLL_MS = 3_000;
export const WHITELIST_EXPECT_MAX_MS = 5 * 60_000;
/** A payment the sheet saw recorded, to this address, at this time: the list waits to see its stage move. */
export type StageExpected = { id: number; since: number } | null;

type StagedEntry = Pick<WhitelistEntry, 'status'> & { id?: number; stage?: WhitelistEntry['stage'] };
/**
 * Whether the address book is read again, and how soon (Screens' re-review of 55ecf55, #2): while a test is out;
 * and while a payment the sheet saw recorded is not yet seen in the list — the POST's own answer can come before
 * its commit, so the refresh it causes may still read Approved.
 */
export function whitelistPollMs(entries: ReadonlyArray<StagedEntry> | undefined, expected: StageExpected = null, now = Date.now()): number | false {
  if (expectedPending(entries, expected, now)) return WHITELIST_EXPECT_POLL_MS;
  return (entries ?? []).some((e) => whitelistGroup(e) === 'test_sent') ? WHITELIST_TEST_POLL_MS : false;
}
/** The expected address is still read as Approved (or not read yet), and the wait has not run out. */
export function expectedPending(entries: ReadonlyArray<StagedEntry> | undefined, expected: StageExpected, now = Date.now()): boolean {
  if (expected === null || now - expected.since > WHITELIST_EXPECT_MAX_MS) return false;
  const e = (entries ?? []).find((x) => x.id === expected.id);
  return !e || whitelistGroup(e) === 'approved';
}

/** When a test landed: the listing's landing time (stamp or swept payment), else the stamp from an older Worker. */
export const landedAtOf = (e: Pick<WhitelistEntry, 'firstPayoutAt'> & { landedAt?: string | null }): string | null => e.landedAt ?? e.firstPayoutAt ?? null;

/**
 * The one Received that is the screen's primary action (Screens' round on db/0098, #7): the first landed test, in
 * list order — none while the proposal form is open, whose Propose is then the screen's gold button (re-review, #3).
 */
export function primaryReceivedId(
  entries: ReadonlyArray<Pick<WhitelistEntry, 'id' | 'status' | 'firstPayoutAt'> & { stage?: WhitelistEntry['stage']; landedAt?: string | null }>,
  { formOpen = false }: { formOpen?: boolean } = {},
): number | null {
  if (formOpen) return null;
  return entries.find((e) => whitelistGroup(e) === 'test_sent' && landedAtOf(e) !== null)?.id ?? null;
}

/**
 * A payment this sheet now sees recorded — by the door's answer, by asking the desk under its key, or by a void
 * that found it recorded — that it has not said yet: the payee's id, else null. The sheet says it once per key
 * (re-review #2: the whitelist must hear of a test whose POST answered before it committed).
 */
export function newlyRecorded(attempt: { state: string; requestId: string; toWalletId: number } | null, toldKey: string | null): number | null {
  return attempt && attempt.state === 'queued' && attempt.requestId !== toldKey ? attempt.toWalletId : null;
}

/** The Flow's Pay step: a read that failed or is still running is said as that — never as "no payable address". */
export function payStepStatus(i: { loading: boolean; error: string | null; payable: number; hotWord: string }): { status: string; bad: string | null } {
  if (i.error !== null) return { status: "Couldn't read the address book — try again.", bad: i.error };
  if (i.loading) return { status: 'Reading…', bad: null };
  if (i.payable === 0) return { status: 'No payable address yet.', bad: null };
  return { status: `Pay from ${i.hotWord}: Pay on the entry, sign in Approvals.`, bad: null };
}

/**
 * A pool's total: the sum of what was READ, and how many of the wallets that
 * is. A null balance means the chain was not reached, not that the wallet is
 * empty — so with nothing read the total is null ("Not known"), never 0, and
 * with some unread the figure is a subtotal the screen labels as such.
 * Summed as integers scaled to the widest fraction seen: no float touches it.
 */
export function poolTotal(rows: Pick<WalletBalance, 'balance'>[]): { total: string | null; read: number; all: number } {
  const known = rows.filter((r): r is { balance: string } => r.balance !== null);
  if (known.length === 0) return { total: null, read: 0, all: rows.length };
  const places = known.reduce((m, r) => Math.max(m, (r.balance.split('.')[1] ?? '').length), 0);
  const scaled = known.reduce((acc, r) => {
    const [w, f = ''] = r.balance.split('.');
    return acc + BigInt(w + f.padEnd(places, '0'));
  }, 0n);
  const s = scaled.toString().padStart(places + 1, '0');
  const whole = s.slice(0, s.length - places) || '0';
  const frac = places ? s.slice(s.length - places).replace(/0+$/, '') : '';
  return { total: frac ? `${whole}.${frac}` : whole, read: known.length, all: rows.length };
}

/**
 * Where a deposit's card stands, by the card's own word (pay sheet round 8, #4; Astra round c, C4) — one answer
 * the step line, the "ready" count, the waiting count and the link all read:
 *   open      awaiting his signature and not expired — sign it in Approvals
 *   signed    signed (ready, broadcast, confirmed): the desk sends it — nothing for him
 *   expired   its window passed — rebuilt when he starts signing (waits on him)
 *   replaced  superseded by a rebuild (or refused): the desk builds a new one
 *   missing   no card read for the sweep yet
 * null when the sweep is not at the card (awaiting_approval). An older Worker that sends no card: open, as before.
 */
export type CardState = 'open' | 'signed' | 'expired' | 'replaced' | 'missing';
type SweepCard = { status: string; card?: { status: string | null; expiresAt: string | null } | null };
export function cardStateOf(sweep: SweepCard | null | undefined, now = Date.now()): CardState | null {
  if (!sweep || sweep.status !== 'awaiting_approval') return null;
  if (sweep.card === undefined) return 'open';
  const c = sweep.card;
  if (!c) return 'missing';
  switch (c.status) {
    case 'awaiting_cosign': {
      const t = c.expiresAt ? Date.parse(c.expiresAt) : NaN;
      return Number.isFinite(t) && t > now ? 'open' : 'expired';
    }
    case 'ready': case 'broadcast': case 'confirmed': return 'signed';
    case 'expired': return 'expired';
    case 'superseded': case 'rejected': return 'replaced';
    default: return 'missing';
  }
}
/** A card he can sign now. */
export const cardReady = (sweep: SweepCard | null | undefined, now = Date.now()): boolean => cardStateOf(sweep, now) === 'open';

/**
 * The approval window as the desk holds it (pay sheet round 8, #3): open until its expiry — read from the desk, not
 * from the button's last press, which stayed "Window open" for good after the window had closed.
 */
export function windowOpenUntil(w: { window: { expiresAt: string } | null } | undefined, now = Date.now()): string | null {
  const at = w?.window?.expiresAt;
  if (!at) return null;
  const t = Date.parse(at);
  return Number.isFinite(t) && t > now ? at : null;
}

/** One run at a time, decided SYNCHRONOUSLY before the first await: a second call while one runs is dropped (the cable
 *  sheet's ceremony; Screens and Astra, 27 Oct 2026, #4 — two taps during a slow card read started two). */
export function oneAtATime(): (run: () => Promise<void>) => Promise<void> {
  let busy = false;
  return async (run) => {
    if (busy) return;
    busy = true;
    try { await run(); } finally { busy = false; }
  };
}

/** A read a save may stand on: it has data, its last fetch did not fail, and it is not paused offline (Settings). */
export function readIsCurrent(q: { data: unknown; isError: boolean; fetchStatus: string }): boolean {
  return q.data !== undefined && !q.isError && q.fetchStatus !== 'paused';
}

/** The Pay page's destination, from the address book entry WHATEVER its stage now — an attempt under way is settled by
 *  its id after the stage moved; only a NEW payment depends on the stage (MovePages; Screens and Astra, 27 Oct 2026, #1). */
export function moveDestinationOf(e: WhitelistEntry | null, testPaymentUsdt: string):
  { id: number; name: string; address: string; stage: WhitelistEntry['stage'] | null; testPaymentUsdt: string } | null {
  return e ? { id: e.id, name: e.partyName, address: e.address, stage: e.stage ?? null, testPaymentUsdt } : null;
}
